/**
 * Partner and staff accounts (P0-BR1, D-016, §1.4).
 *
 * The farm started with four partners, but four was never a rule — ownership
 * is derived from capital, not headcount (BR-01), so the system has always been
 * indifferent to how many there are. What was missing was a way to say so:
 * without this screen, adding a partner meant editing the seed and redeploying.
 *
 * Two audiences, one screen. Every partner can read the roster, because §1.4
 * makes read transparency absolute and "who has access to the books" is exactly
 * the kind of thing that should not be private. Only an admin sees the
 * controls.
 *
 * The controls an admin must never aim at themselves — changing their own role,
 * deactivating their own account — are simply not rendered on their own row.
 * That is courtesy, not enforcement: `users.service.ts` refuses both regardless
 * of what arrives, and it is the reason the farm cannot end up with zero
 * admins. Hiding a button a user should not press is UX; the rule is the
 * server's (AP-1).
 *
 * Nobody is ever deleted. A partner who leaves is deactivated (BR-08) — their
 * capital, drawings and approvals stay attributable, and they keep appearing on
 * the Partners screen for as long as their money is still in the farm.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { useAuth } from '../auth/AuthContext.js';
import { useCreateUser, useUpdateUser, useUsers, type AssignableRole, type User } from './api.js';

const ROLES: AssignableRole[] = ['partner', 'staff', 'admin'];

/** Chips rather than a select: one tap, and every option visible without it. */
function RoleChips({
  value,
  onChange,
  label,
}: {
  value: AssignableRole;
  onChange: (role: AssignableRole) => void;
  label: string;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <fieldset className="mb-4">
      <legend className="mb-2 text-sm font-medium text-gray-700">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {ROLES.map((role) => (
          <button
            key={role}
            type="button"
            onClick={() => onChange(role)}
            aria-pressed={value === role}
            className={`min-h-touch rounded-full px-4 py-2 text-sm ${
              value === role ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t(`users.role.${role}`)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function FieldError({ message }: { message: string | undefined }): JSX.Element | null {
  if (message === undefined) return null;
  return <p className="mt-1 text-sm text-red-700">{message}</p>;
}

/**
 * Adding an account (P0-BR1 — no public signup).
 *
 * The password is set by the admin and handed over out of band. There is no
 * invite email because the farm has no mail server and a partner standing in a
 * shed has no inbox open; a phone number and a spoken password is how this
 * actually happens.
 */
function AddUserForm({ onAdded }: { onAdded: () => void }): JSX.Element {
  const { t } = useTranslation();
  const createUser = useCreateUser();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<AssignableRole>('partner');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function submit(): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});

    try {
      await createUser.mutateAsync({
        fullName: fullName.trim(),
        phone: phone.trim(),
        password,
        role,
        // Bangla is the default locale; the new partner can change it (P0-BR4).
        language: 'bn',
      });

      // Only now is anything cleared — a rejected submit keeps everything typed.
      setFullName('');
      setPhone('');
      setPassword('');
      setRole('partner');
      onAdded();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form
      className="mb-5 rounded-xl bg-white p-4 shadow-sm"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <h2 className="mb-3 text-base font-semibold text-gray-800">{t('users.addTitle')}</h2>

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="new-name">
        {t('users.fullName')}
      </label>
      <input
        id="new-name"
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        maxLength={100}
        className="mb-1 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      <FieldError message={fieldErrors['fullName']} />

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="new-phone">
        {t('users.phone')}
      </label>
      <input
        id="new-phone"
        type="tel"
        inputMode="numeric"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
        placeholder="01XXXXXXXXX"
        className="mb-1 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      <FieldError message={fieldErrors['phone']} />

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="new-password">
        {t('users.password')}
      </label>
      <input
        id="new-password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
        className="min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      <p className="mb-1 mt-1 text-xs text-gray-500">{t('users.passwordHint')}</p>
      <FieldError message={fieldErrors['password']} />

      <div className="mt-4">
        <RoleChips value={role} onChange={setRole} label={t('users.role')} />
      </div>

      {errorKey !== null && (
        <div role="alert" className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={createUser.isPending}
        className="min-h-[52px] w-full rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
      >
        {t(createUser.isPending ? 'users.saving' : 'users.save')}
      </button>
    </form>
  );
}

/**
 * Editing one account.
 *
 * `isSelf` removes the role chips and the deactivate button rather than
 * disabling them — a disabled control invites a partner to work out why, and
 * the answer ("ask the other admin") is better said by not offering it.
 */
function EditUserForm({ user, isSelf, onDone }: { user: User; isSelf: boolean; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const updateUser = useUpdateUser();

  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email ?? '');
  const [role, setRole] = useState<AssignableRole>(user.role);
  const [password, setPassword] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function save(changes: Parameters<typeof updateUser.mutateAsync>[0]['changes']): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});

    try {
      await updateUser.mutateAsync({ id: user.id, changes });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form
      className="mt-3 border-t border-gray-100 pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        void save({
          fullName: fullName.trim(),
          // An empty box means "no email", which is a clear rather than a skip.
          email: email.trim() === '' ? null : email.trim(),
          ...(isSelf ? {} : { role }),
          ...(password === '' ? {} : { password }),
        });
      }}
      noValidate
    >
      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={`name-${String(user.id)}`}>
        {t('users.fullName')}
      </label>
      <input
        id={`name-${String(user.id)}`}
        value={fullName}
        onChange={(event) => setFullName(event.target.value)}
        maxLength={100}
        className="mb-1 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      <FieldError message={fieldErrors['fullName']} />

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor={`email-${String(user.id)}`}>
        {t('users.email')}
      </label>
      <input
        id={`email-${String(user.id)}`}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        className="mb-1 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      <FieldError message={fieldErrors['email']} />

      <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor={`password-${String(user.id)}`}>
        {t('users.newPassword')}
      </label>
      <input
        id={`password-${String(user.id)}`}
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
        className="min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      {/* Says out loud what the server does anyway: a reset ends every session
          that partner has open, so a 30-day refresh token cannot outlive it. */}
      <p className="mb-1 mt-1 text-xs text-gray-500">{t('users.resetHint')}</p>
      <FieldError message={fieldErrors['password']} />

      {!isSelf && (
        <div className="mt-4">
          <RoleChips value={role} onChange={setRole} label={t('users.role')} />
        </div>
      )}

      {errorKey !== null && (
        <div role="alert" className="mb-3 mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={updateUser.isPending}
          className="min-h-touch flex-1 rounded-xl bg-green-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(updateUser.isPending ? 'users.saving' : 'users.save')}
        </button>

        <button
          type="button"
          onClick={onDone}
          className="min-h-touch rounded-xl bg-gray-100 px-4 py-2 text-sm text-gray-700"
        >
          {t('app.cancel')}
        </button>
      </div>

      {/* Deactivation is separated from the save button on purpose: it is the
          one action here a mis-tap should not be able to reach. */}
      {!isSelf && (
        <button
          type="button"
          onClick={() => void save({ isActive: !user.isActive })}
          disabled={updateUser.isPending}
          className={`mt-2 min-h-touch w-full rounded-xl px-4 py-2 text-sm disabled:opacity-50 ${
            user.isActive ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
          }`}
        >
          {t(user.isActive ? 'users.deactivate' : 'users.reactivate')}
        </button>
      )}
    </form>
  );
}

function UserRow({ user, canManage, isSelf }: { user: User; canManage: boolean; isSelf: boolean }): JSX.Element {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  return (
    <li className={`rounded-xl bg-white p-4 shadow-sm ${user.isActive ? '' : 'opacity-70'}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-800">
            {user.fullName}
            {isSelf && <span className="ml-2 text-xs font-normal text-gray-500">{t('users.you')}</span>}
          </p>
          <p className="text-xs text-gray-500">{user.phone}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {!user.isActive && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{t('users.inactive')}</span>
          )}
          <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-800">
            {t(`users.role.${user.role}`)}
          </span>
        </div>
      </div>

      {canManage && !isEditing && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
        >
          {t('users.edit')}
        </button>
      )}

      {canManage && isEditing && <EditUserForm user={user} isSelf={isSelf} onDone={() => setIsEditing(false)} />}
    </li>
  );
}

export function UsersScreen(): JSX.Element {
  const { t } = useTranslation();
  const { user: signedIn } = useAuth();
  const { data: users = [], isLoading, isError, refetch } = useUsers();
  const [isAdding, setIsAdding] = useState(false);

  const isAdmin = signedIn?.role === 'admin';

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('users.title')}</h1>

      {isAdmin ? (
        isAdding ? (
          <AddUserForm onAdded={() => setIsAdding(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="mb-4 flex min-h-touch w-full items-center justify-between rounded-xl bg-green-700 px-4 py-3 text-white"
          >
            <span className="text-sm font-medium">{t('users.add')}</span>
            <span aria-hidden="true">+</span>
          </button>
        )
      ) : (
        // Not an error and not a locked door: everyone is meant to see who has
        // access to the books (§1.4). This says why there are no buttons.
        <p className="mb-4 rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-600">{t('users.readOnly')}</p>
      )}

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      <ul className="space-y-3">
        {users.map((user) => (
          <UserRow key={user.id} user={user} canManage={isAdmin} isSelf={user.id === signedIn?.id} />
        ))}
      </ul>

      {users.length > 0 && <p className="mt-4 text-xs text-gray-500">{t('users.deactivateNote')}</p>}
    </div>
  );
}
